import type { Data } from "app/modules/api-client/queryTypes";

import { type AiAdsDisplay, getAiAdsDisplay, getLeadingAssistantName } from "./getAiInsightsDisplay";

type AiAdRecord = Data<"AdIntAiAds", "adintelligenceAiadsGalleryList">["data"][number];
type OverviewRecord = Data<"AiRefferrals", "aiResearchAiTrafficOverviewList">["records"][number];

export type AiInsightsCardStatus = "upgrade" | "error" | "empty" | "content";

export type AiInsightsTrafficContent = {
    leadingAssistant: string;
};

export type AiInsightsContent = {
    isTrafficLocked: boolean;
    isAdsLocked: boolean;
    traffic: AiInsightsTrafficContent | null;
    ads: AiAdsDisplay | null;
};

export type AiInsightsViewModel = {
    status: AiInsightsCardStatus;
    content: AiInsightsContent | null;
};

export type GetAiInsightsViewModelParams = {
    trafficRecords: OverviewRecord[] | undefined;
    ads: AiAdRecord[];
    isAdsGalleryTruncated: boolean;
    isTrafficError: boolean;
    isAdsError: boolean;
    isTrafficLocked: boolean;
    isAdsLocked: boolean;
    isUpgrade: boolean;
};

export const getAiInsightsViewModel = ({
    trafficRecords,
    ads,
    isAdsGalleryTruncated,
    isTrafficError,
    isAdsError,
    isTrafficLocked,
    isAdsLocked,
    isUpgrade,
}: GetAiInsightsViewModelParams): AiInsightsViewModel => {
    const leadingAssistant = getLeadingAssistantName(trafficRecords);
    const adsDisplay = getAiAdsDisplay(ads, isAdsGalleryTruncated);
    const hasTraffic = Boolean(leadingAssistant) && !isTrafficError;
    const hasAds = Boolean(adsDisplay) && !isAdsError;
    const hasLockedSection = isTrafficLocked || isAdsLocked;
    const showUpgrade = isUpgrade || (hasLockedSection && !hasTraffic && !hasAds);
    const isRequestError = (isTrafficError || isAdsError) && !hasTraffic && !hasAds && !hasLockedSection;

    if (showUpgrade) {
        return { status: "upgrade", content: null };
    }

    if (isRequestError) {
        return { status: "error", content: null };
    }

    if (!hasTraffic && !hasAds && !hasLockedSection) {
        return { status: "empty", content: null };
    }

    return {
        status: "content",
        content: {
            isTrafficLocked,
            isAdsLocked,
            traffic: hasTraffic && leadingAssistant ? { leadingAssistant } : null,
            ads: hasAds && adsDisplay ? adsDisplay : null,
        },
    };
};
